import {Popover} from '@mui/material';
import {ModalProps} from '@mui/material/Modal';
import {
  PopoverOrigin,
  PopoverPosition,
  PopoverReference,
} from '@mui/material/Popover/Popover';
import React from 'react';

type EPopoverProps = {
  /**
   * An HTML element, or a function that returns one.
   * It's used to set the position of the popover.
   */
  anchorEl?: null | Element | (() => Element);
  /**
   * This is the point on the anchor where the popover's
   * `anchorEl` will attach to. This is not used when the
   * anchorReference is 'anchorPosition'.
   *
   * Options:
   * vertical: [top, center, bottom];
   * horizontal: [left, center, right].
   * @default {
   *   vertical: 'top',
   *   horizontal: 'left',
   * }
   */
  anchorOrigin?: PopoverOrigin;
  /**
   * This is the position that may be used to set the position of the popover.
   * The coordinates are relative to the application's client area.
   */
  anchorPosition?: PopoverPosition;
  /**
   * This determines which anchor prop to refer to when setting
   * the position of the popover.
   * @default 'anchorEl'
   */
  anchorReference?: PopoverReference;
  /**
   * The content of the component.
   */
  children?: React.ReactNode;
  onClose?: ModalProps['onClose'];
  /**
   * If `true`, the component is shown.
   */
  open: boolean;
  /**
   * This is the point on the popover which
   * will attach to the anchor's origin.
   *
   * Options:
   * vertical: [top, center, bottom, x(px)];
   * horizontal: [left, center, right, x(px)].
   * @default {
   *   vertical: 'top',
   *   horizontal: 'left',
   * }
   */
  transformOrigin?: PopoverOrigin;
  /**
   * If true, the backdrop is not rendered.
   */
  hideBackdrop?: boolean;
  disablePortal?: boolean;
  width?: string;
  height?: string;
  /**
   * If true, the modal will not automatically shift focus to itself when it opens,
   * and replace it to the last focused element when it closes.
   * Generally this should not be set to true and is set to false by default.
   */
  disableAutoFocus?: boolean;
  /**
   * If true, the modal will not prevent focus from leaving the modal while open.
   * Generally this should not be set to true and is set to false by default.
   */
  disableEnforceFocus?: boolean;
};

export const EPopover = (props: EPopoverProps): React.ReactElement => (
  <Popover
    elevation={0}
    slotProps={{
      paper: {
        style: {
          maxHeight: 'none',
          maxWidth: 'none',
          pointerEvents: 'auto',
          borderRadius: 'var(--popover-border-radius)',
          backgroundColor: 'transparent',
          width: props.width,
          height: props.height,
        },
        onClick: (evt) =>
          props.onClose ? props.onClose(evt, 'backdropClick') : {},
      },
    }}
    transitionDuration={0}
    {...props}
    style={{pointerEvents: props.hideBackdrop ? 'none' : 'auto'}}
  />
);
